import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { format, subDays, eachDayOfInterval, startOfMonth, endOfMonth, getDate, subMonths } from 'date-fns';
import { pl } from 'date-fns/locale';

// Rejestracja komponentów Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const SalesCharts = ({ data, products = [], selectedMonth, chartType = 'monthly', employeeId = null }) => {
  const theme = useTheme();
  const [chartData, setChartData] = useState({
    daily: null,
    monthly: null,
    locations: null,
    locations: null
  });
  const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'quarter', 'year'

  // Kolory dla wykresów
  const chartColors = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    background: theme.palette.background.paper,
    text: theme.palette.text.primary,
    grid: theme.palette.divider
  };

  // Przygotowanie danych do wykresów
  useEffect(() => {
    if (!data || data.length === 0) {
      setChartData({ daily: null, monthly: null, locations: null, categories: null });
      return;
    }

    // KROK 1: Filtruj dane, aby usunąć sprzedaże bez prawidłowej daty.
    const validData = data.filter(sale => sale.completedAt && sale.completedAt instanceof Date && !isNaN(sale.completedAt.getTime()));

    // KROK 2: Filtruj dane dla konkretnego pracownika, jeśli podano ID.
    const employeeFilteredData = employeeId 
      ? validData.filter(sale => sale.employeeId === employeeId)
      : validData;

    // KROK 3: Przygotuj dane do wykresów, używając przefiltrowanych danych.
    prepareDailyChart(employeeFilteredData);
    prepareMonthlyChart(employeeFilteredData);
    prepareLocationsChart(employeeFilteredData);


  }, [data, products, selectedMonth, timeRange, employeeId]);

  // Funkcja przygotowująca dane dla wykresu dziennego
  const prepareDailyChart = (salesData) => {
    // Określenie zakresu dat
    let startDate, endDate;
    const now = new Date();
    
    switch (timeRange) {
      case 'week':
        startDate = subDays(now, 7);
        endDate = now;
        break;
      case 'month':
        startDate = startOfMonth(selectedMonth);
        endDate = endOfMonth(selectedMonth);
        break;
      case 'quarter':
        startDate = subDays(now, 90);
        endDate = now;
        break;
      case 'year':
        startDate = subDays(now, 365);
        endDate = now;
        break;
      default:
        startDate = startOfMonth(selectedMonth);
        endDate = endOfMonth(selectedMonth);
    }
    
    // Generowanie tablicy dni w wybranym zakresie
    const daysArray = eachDayOfInterval({ start: startDate, end: endDate });
    
    // Przygotowanie danych dla każdego dnia
    const dailyAmounts = daysArray.map(day => {
      const dayStr = format(day, 'yyyy-MM-dd');
      const daySales = salesData.filter(sale => {
        const saleDate = sale.completedAt;
        return saleDate && format(saleDate, 'yyyy-MM-dd') === dayStr;
      });
      
      const totalAmount = daySales.reduce((sum, sale) => sum + (parseFloat(sale.amount) || 0), 0);
      return { day, totalAmount };
    });
    
    // Przygotowanie danych dla wykresu
    const dailyChartData = {
      labels: dailyAmounts.map(item => format(item.day, 'd MMM', { locale: pl })),
      datasets: [
        {
          label: 'Sprzedaż dzienna',
          data: dailyAmounts.map(item => item.totalAmount),
          borderColor: chartColors.primary,
          backgroundColor: `${chartColors.primary}33`,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: chartColors.primary,
          pointBorderColor: theme.palette.background.paper,
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
    
    setChartData(prev => ({ ...prev, daily: dailyChartData }));
  };

  // Funkcja przygotowująca dane dla wykresu miesięcznego
  const prepareMonthlyChart = (salesData) => {
    const monthsArray = Array.from({ length: 12 }, (_, i) => format(subMonths(new Date(), i), 'yyyy-MM')).reverse();
    
    const monthlyAmounts = monthsArray.map(monthStr => {
      const monthSales = salesData.filter(sale => {
        const saleDate = sale.completedAt;
        return saleDate && format(saleDate, 'yyyy-MM') === monthStr;
      });
      const totalAmount = monthSales.reduce((sum, sale) => sum + (parseFloat(sale.amount) || 0), 0);
      return { month: monthStr, totalAmount };
    });

    const monthlyChartData = {
      labels: monthlyAmounts.map(item => format(new Date(item.month), 'MMM yyyy', { locale: pl })),
      datasets: [
        {
          label: 'Sprzedaż miesięczna',
          data: monthlyAmounts.map(item => item.totalAmount),
          backgroundColor: chartColors.secondary,
          borderColor: chartColors.secondary,
          borderWidth: 1,
        },
      ],
    };
    
    setChartData(prev => ({ ...prev, monthly: monthlyChartData }));
  };

  // Funkcja przygotowująca dane dla wykresu lokalizacji
  const prepareLocationsChart = (salesData) => {
    const locationsData = salesData.reduce((acc, sale) => {
      const location = sale.location || 'Brak lokalizacji';
      if (!acc[location]) {
        acc[location] = { totalAmount: 0, count: 0 };
      }
      acc[location].totalAmount += parseFloat(sale.amount) || 0;
      acc[location].count += 1;
      return acc;
    }, {});

    const locationsChartData = {
      labels: Object.keys(locationsData),
      datasets: [
        {
          label: 'Wartość sprzedaży',
          data: Object.values(locationsData).map(loc => loc.totalAmount),
          backgroundColor: [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.success.light,
            theme.palette.error.light,
            theme.palette.warning.light,
            theme.palette.info.light,
          ],
          borderColor: theme.palette.background.paper,
          borderWidth: 2,
          hoverOffset: 10
        }
      ]
    };
    
    setChartData(prev => ({ ...prev, locations: locationsChartData }));
  };

  // Opcje dla wykresów
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: chartColors.text,
          font: {
            family: theme.typography.fontFamily,
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: chartColors.background,
        titleColor: chartColors.text,
        bodyColor: chartColors.text,
        borderColor: chartColors.grid,
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${new Intl.NumberFormat('pl-PL', {
              style: 'currency',
              currency: 'PLN'
            }).format(context.raw)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: chartColors.grid,
          drawBorder: false
        },
        ticks: {
          color: chartColors.text,
          font: {
            family: theme.typography.fontFamily
          }
        }
      },
      y: {
        grid: {
          color: chartColors.grid,
          drawBorder: false
        },
        ticks: {
          color: chartColors.text,
          font: {
            family: theme.typography.fontFamily
          },
          callback: function(value) {
            return new Intl.NumberFormat('pl-PL', {
              style: 'currency',
              currency: 'PLN',
              notation: 'compact'
            }).format(value);
          }
        }
      }
    }
  };

  const barChartOptions = {
    ...lineChartOptions,
    scales: {
      ...lineChartOptions.scales,
      x: {
        ...lineChartOptions.scales.x,
        stacked: false
      },
      y: {
        ...lineChartOptions.scales.y,
        stacked: false
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: chartColors.text,
          font: {
            family: theme.typography.fontFamily,
            size: 12
          },
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: chartColors.background,
        titleColor: chartColors.text,
        bodyColor: chartColors.text,
        borderColor: chartColors.grid,
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw;
            const total = context.chart.getDatasetMeta(0).total;
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${new Intl.NumberFormat('pl-PL', {
              style: 'currency',
              currency: 'PLN'
            }).format(value)} (${percentage}%)`;
          }
        }
      }
    }
  };

  // Obsługa zmiany zakresu czasu
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  // Renderowanie wykresów w zależności od typu
  const renderCharts = () => {
    if (chartType === 'monthly') {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card elevation={3}>
              <CardHeader 
                title="Sprzedaż w ciągu ostatnich 30 dni"
                action={
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Zakres</InputLabel>
                    <Select value={timeRange} onChange={handleTimeRangeChange} label="Zakres">
                      <MenuItem value="week">Tydzień</MenuItem>
                      <MenuItem value="month">Miesiąc</MenuItem>
                      <MenuItem value="quarter">Kwartał</MenuItem>
                      <MenuItem value="year">Rok</MenuItem>
                    </Select>
                  </FormControl>
                }
              />
              <CardContent sx={{ height: 350 }}>
                {chartData.daily ? (
                  <Line data={chartData.daily} options={lineChartOptions} />
                ) : (
                  <Typography>Brak danych do wyświetlenia</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );
    }
    
    if (chartType === 'detailed') {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card elevation={3} sx={{ height: '100%' }}>
              <CardHeader title="Sprzedaż dzienna" />
              <CardContent sx={{ height: 300 }}>
                {chartData.daily ? (
                  <Line data={chartData.daily} options={lineChartOptions} />
                ) : (
                  <Typography>Brak danych do wyświetlenia</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ height: '100%' }}>
              <CardHeader title="Sprzedaż według lokalizacji" />
              <CardContent sx={{ height: 300 }}>
                {chartData.locations ? (
                  <Pie data={chartData.locations} options={pieChartOptions} />
                ) : (
                  <Typography>Brak danych do wyświetlenia</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%' }}>
              <CardHeader title="Sprzedaż w miesiącu" />
              <CardContent sx={{ height: 300 }}>
                {chartData.monthly ? (
                  <Bar data={chartData.monthly} options={barChartOptions} />
                ) : (
                  <Typography>Brak danych do wyświetlenia</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          

        </Grid>
      );
    }
    
    if (chartType === 'employee') {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card elevation={3}>
              <CardHeader title="Twoja sprzedaż dzienna" />
              <CardContent sx={{ height: 300 }}>
                {chartData.daily ? (
                  <Line data={chartData.daily} options={lineChartOptions} />
                ) : (
                  <Typography>Brak danych do wyświetlenia</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardHeader title="Twoja sprzedaż według lokalizacji" />
              <CardContent sx={{ height: 300 }}>
                {chartData.locations ? (
                  <Pie data={chartData.locations} options={pieChartOptions} />
                ) : (
                  <Typography>Brak danych do wyświetlenia</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardHeader title="Twoja sprzedaż według kategorii" />
              <CardContent sx={{ height: 300 }}>
                {chartData.categories ? (
                  <Pie data={chartData.categories} options={pieChartOptions} />
                ) : (
                  <Typography>Brak danych do wyświetlenia</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );
    }
    
    return null;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Statystyki sprzedaży - {format(selectedMonth, 'LLLL yyyy', { locale: pl })}
      </Typography>
      
      {renderCharts()}
    </Box>
  );
};

export default SalesCharts;
